import { DialogComponent, DialogOpenEvent } from '@theme/dialog';

/** Links to this hash open the panel: the footer's Cookie preferences menu
 * item, the privacy policy. */
const PREFERENCES_HASH = '#cookies';

/**
 * `?birrot_consent=show` shows the banner whatever the region, for the rest
 * of the tab's session, until a choice is recorded: for checking it from
 * outside the banner regions. It grants nothing.
 */
const FORCE_PARAM = 'birrot_consent';
const FORCE_KEY = 'birrot-consent-force';

/**
 * A choice sent but not yet confirmed; a navigation can cancel the save
 * (Safari), and the next page sends it again.
 */
const PENDING_KEY = 'birrot-consent-pending';

/**
 * On `<html>` where consent applies: the visitor is in a banner region, or
 * has made a choice. Without it, birrot-consent.liquid hides the footer's
 * Cookie preferences menu item.
 */
const REQUIRED_CLASS = 'birrot-consent-required';

/**
 * @typedef {{ preferences: boolean, analytics: boolean, marketing: boolean }} Consent
 */

/** The purposes a choice here records (`sale_of_data` is never set). */
const PURPOSES = ['preferences', 'analytics', 'marketing'];

/**
 * Whether a choice is recorded: Shopify gives each purpose 'yes', 'no' or ''.
 * @param {Record<string, string>} consent - `currentVisitorConsent()`.
 */
function hasChoice(consent) {
  return PURPOSES.some((purpose) => (consent[purpose] ?? '') !== '');
}

/**
 * Whether Shopify holds this choice: each purpose's 'yes' or 'no' against it.
 * @param {Record<string, string>} consent - `currentVisitorConsent()`.
 * @param {Consent} choice
 */
function matches(consent, choice) {
  return PURPOSES.every((purpose) => consent[purpose] === (choice[purpose] ? 'yes' : 'no'));
}

/**
 * @param {Consent | null} choice - The choice to keep, or null to drop it.
 */
function setPending(choice) {
  try {
    if (choice) sessionStorage.setItem(PENDING_KEY, JSON.stringify(choice));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {}
}

/**
 * The cookie consent banner and preferences panel
 * (snippets/birrot-consent.liquid, docs/adr/0011).
 *
 * Shopify holds the consent. The banner shows where the Customer Privacy
 * API's `shouldShowBanner()` is true (the regions set in admin) and
 * `currentVisitorConsent()` holds no choice yet; so it comes back on every
 * page until the visitor chooses, with nothing stored here.
 * `shouldShowBanner()` stays true after a choice (seen live, 2026-10-21).
 * A choice goes to `setTrackingConsent`, which Shopify uses to load or hold back the store's Web Pixels (the Meta and
 * Google channels among them) and records against `consentId()`.
 *
 * - Accept and Decline on the banner record a choice; Manage opens the
 *   panel.
 * - The panel is a modal dialog (DialogComponent: scroll lock, Escape, a
 *   click on the backdrop). Its switches start at the visitor's consent, so
 *   off for a first visit. Close leaves without recording anything.
 * - Anything can open the panel: `on:click="#birrot-consent/openPanel"` or
 *   a link to `#cookies` (the footer's Cookie preferences, the privacy policy).
 * - If the API does not load, the banner stays hidden and nothing is
 *   granted: with no consent Shopify keeps the pixels back in the banner
 *   regions. A later click tries to load it again.
 * - Outside the banner regions consent does not apply: no banner, no Cookie
 *   preferences in the footer (REQUIRED_CLASS).
 * - A choice is kept in sessionStorage (PENDING_KEY) until Shopify confirms
 *   it. Safari cancels the save when a navigation starts before its answer,
 *   and the next page then had no choice and showed the banner again (seen
 *   live, 2026-10-21); that page sends the kept choice again instead, the
 *   banner hidden, and shows it only if the resend is refused.
 * - The kept choice is per tab: a newer choice made in another tab is
 *   overwritten when this tab resends its own.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} banner - The bottom-right banner.
 * @property {HTMLDialogElement} dialog - The preferences panel.
 * @property {HTMLFormElement} form - The panel's switches.
 *
 * @extends {DialogComponent}
 */
class BirrotConsent extends DialogComponent {
  requiredRefs = ['banner', 'dialog', 'form'];

  /** @type {Promise<any> | null} */
  #privacy = null;

  /** True while #record runs: visitorConsentCollected then leaves the banner to it. */
  #recording = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.#focusDialog);
    document.addEventListener('click', this.#onLinkClick);
    document.addEventListener('visitorConsentCollected', this.#onConsentCollected);

    // The theme editor previews the page, not a visitor.
    if (window.Shopify?.designMode) return;

    this.#api()
      .then((privacy) => {
        // shouldShowBanner() is the region alone: it stays true after a
        // choice, so the banner also waits for none to be recorded.
        const region = privacy.shouldShowBanner();
        const consent = privacy.currentVisitorConsent();
        // A choice whose save a navigation cut off: it is the visitor's.
        const pending = this.#pending();
        const resend = pending !== null && !matches(consent, pending);
        if (pending && !resend) setPending(null);
        const chosen = hasChoice(consent) || resend;
        const forced = this.#forced(privacy);
        const show = !resend && ((region && !chosen) || forced);
        if (show) this.refs.banner.hidden = false;
        document.documentElement.classList.toggle(REQUIRED_CLASS, region || chosen || show);
        if (resend) this.#record(pending, { resend: true });
      })
      .catch(() => {})
      // The panel opens for the hash even when the API failed: its switches
      // then start off.
      .finally(() => {
        if (window.location.hash === PREFERENCES_HASH) this.openPanel();
      });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#focusDialog);
    document.removeEventListener('click', this.#onLinkClick);
    document.removeEventListener('visitorConsentCollected', this.#onConsentCollected);
  }

  /**
   * Opens the panel with its switches at what is allowed now: the visitor's
   * choice, or with none yet, the region's default (off in a banner region,
   * on outside one, where Shopify allows tracking until told otherwise).
   */
  openPanel = async () => {
    const privacy = await this.#api().catch(() => null);
    const consent = privacy?.currentVisitorConsent() ?? {};
    const required = document.documentElement.classList.contains(REQUIRED_CLASS);

    // Each switch is named for its Customer Privacy API purpose.
    for (const input of this.refs.form.querySelectorAll('input[role="switch"]')) {
      const value = consent[input.name];
      input.checked = value === 'yes' || (privacy !== null && value === '' && !required);
    }

    this.showDialog();
  };

  acceptAll = () => this.#record({ preferences: true, analytics: true, marketing: true });

  rejectAll = () => this.#record({ preferences: false, analytics: false, marketing: false });

  /**
   * @param {SubmitEvent} event
   */
  savePreferences = (event) => {
    event.preventDefault();
    const data = new FormData(this.refs.form);
    const analytics = data.has('analytics');
    const marketing = data.has('marketing');
    // Shopify's `preferences` purpose has no switch of its own: it goes with
    // everything optional, as Accept all grants it.
    this.#record({ preferences: analytics && marketing, analytics, marketing });
  };

  /**
   * @param {Consent} consent
   * @param {{ resend?: boolean }} [options] - `resend`: a pending choice sent
   *   again on load, with the banner hidden.
   */
  async #record(consent, { resend = false } = {}) {
    // Shopify fires visitorConsentCollected for this call too; while it
    // runs, #onConsentCollected leaves the banner alone (see below).
    this.#recording = true;
    setPending(consent);
    try {
      const privacy = await this.#api();
      await new Promise((resolve, reject) => {
        privacy.setTrackingConsent(consent, (/** @type {{ error?: string }} */ result) =>
          result?.error ? reject(new Error(result.error)) : resolve(undefined)
        );
      });
    } catch (error) {
      this.#recording = false;
      // Refused: not sent again silently on a later page. Nothing recorded,
      // so the banner stays (or, for a resend, comes back) for another try.
      setPending(null);
      if (resend) this.refs.banner.hidden = false;
      console.warn('birrot-consent: consent was not recorded', error);
      return;
    }
    setPending(null);

    // Close first: the dialog hands focus back to Manage, in the banner,
    // and hiding the banner then moves it on. Hidden before the close, the
    // banner could not take focus and it fell to the top of the page.
    await this.closeDialog();
    this.#hideBanner();
    this.#recording = false;
  }

  /**
   * The Customer Privacy API, kept once loaded; a failed load is retried on
   * the next call.
   * @returns {Promise<any>}
   */
  #api() {
    this.#privacy ??= new Promise((resolve, reject) => {
      const shopify = window.Shopify;
      if (!shopify?.loadFeatures) {
        reject(new Error('Shopify.loadFeatures is missing'));
        return;
      }
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {unknown} */ error) => {
        if (error || !shopify.customerPrivacy) reject(error ?? new Error('customerPrivacy is missing'));
        else resolve(shopify.customerPrivacy);
      });
    }).catch((error) => {
      // Not cached: the next call tries again.
      this.#privacy = null;
      throw error;
    });
    return this.#privacy;
  }

  /**
   * The choice PENDING_KEY keeps, or null for none or one unreadable.
   * @returns {Consent | null}
   */
  #pending() {
    try {
      const choice = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null');
      if (!choice || !PURPOSES.every((purpose) => typeof choice[purpose] === 'boolean')) return null;
      return choice;
    } catch {
      return null;
    }
  }

  /**
   * @param {any} privacy
   * @returns {boolean}
   */
  #forced(privacy) {
    try {
      const param = new URLSearchParams(window.location.search).get(FORCE_PARAM);
      if (param === 'show') sessionStorage.setItem(FORCE_KEY, '1');
      if (param === 'off') sessionStorage.removeItem(FORCE_KEY);
      if (sessionStorage.getItem(FORCE_KEY) !== '1') return false;
    } catch {
      return false;
    }
    return !hasChoice(privacy.currentVisitorConsent());
  }

  // Any change of consent, Shopify's own banner or another script's
  // included; for a choice made here, #record hides the banner itself
  // once its dialog has closed.
  #onConsentCollected = () => {
    document.documentElement.classList.add(REQUIRED_CLASS);
    if (!this.#recording) this.#hideBanner();
  };

  #hideBanner = () => {
    const { banner } = this.refs;
    if (banner.hidden) return;
    // Focus was on the button just pressed, at the end of the page: hand it
    // to the main content (the skip link's target), not back to the top.
    const hadFocus = banner.contains(document.activeElement);
    banner.hidden = true;
    if (hadFocus) {
      const main = document.getElementById('MainContent');
      if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main?.focus({ preventScroll: true });
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    const link = /** @type {Element} */ (event.target).closest?.('a[href]');
    // A link to the hash on another page navigates, and opens the panel
    // there on load (connectedCallback).
    if (!(link instanceof HTMLAnchorElement) || link.hash !== PREFERENCES_HASH) return;
    if (link.pathname !== window.location.pathname) return;
    // A new tab or window, or a click someone else handled: leave it.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.openPanel();
  };

  /**
   * Opening a dialog focuses its first control, and Safari draws its focus
   * ring (as the cart drawer's): focus the dialog, which announces its title.
   */
  #focusDialog = () => {
    this.refs.dialog.focus();
  };
}

if (!customElements.get('birrot-consent')) {
  customElements.define('birrot-consent', BirrotConsent);
}
